import { useEffect, useState } from "react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Input from "../../../../components/ui/Input";
import LoadingButton from "../../../../components/ui/LoadingButton";
import { useIntl } from "../../../../contexts/IntlContext";
import api from "../../../../services/api";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Painel mínimo, admin-only, para configurar a taxa diária usada na cobrança de uma Live de período
 * (`billedValue = taxa diária × dias`). Não existia nenhuma tela de "Configurações" em app-meulance
 * antes disso; escopo intencionalmente reduzido a este único campo (YAGNI) em vez de um framework de
 * settings genérico.
 */
export default function BillingConfigPanel({ open, onClose }: Props) {
  const intl = useIntl();
  const [dailyRate, setDailyRate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(true);
    api
      .get<{ data: { dailyRate: number } }>("/app/lives/admin/billing-config")
      .then((response) => setDailyRate(String(response.data?.data?.dailyRate ?? "")))
      .catch((err) => setError(err?.response?.data?.message ?? err?.message ?? "Erro ao carregar"))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    const value = Number(dailyRate);
    if (!Number.isFinite(value) || value <= 0) {
      setError(intl.formatMessage({ id: "liveStreams.period.billingConfig.invalidRate" }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put("/app/lives/admin/billing-config", { dailyRate: value });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {intl.formatMessage({ id: "liveStreams.period.billingConfig.title" })}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Input
          size="small"
          type="number"
          label={intl.formatMessage({ id: "liveStreams.period.billingConfig.dailyRate" })}
          value={dailyRate}
          onChange={(e) => setDailyRate(e.target.value)}
          disabled={loading}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} variant="text">
          {intl.formatMessage({ id: "global.cancel" })}
        </LoadingButton>
        <LoadingButton onClick={handleSave} loading={saving} disabled={loading}>
          {intl.formatMessage({ id: "global.save" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
